import { useEffect, useState } from 'react'
import { z } from 'zod'
import {
  Autocomplete,
  Button,
  Combobox,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  TextInput,
  Title,
} from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { useForm, zodResolver } from '@mantine/form'
import {
  parseBalesStringToArray,
  parseBalesArrayToString,
} from '@shared/components/speci/speci-line-editor/bale-number-helpers'
import { BalesList } from '@shared/components/speci/bales-list/bales-list'
import { SpeciLine } from '@shared/components/speci/speci-types'

const INSTRUCTION_OPTIONS_GROWER = [
  'Next Melbourne Sale',
  'Next Christchurch Sale',
  'Hold for more',
  'Hold and test',
  'Please advise',
  'Strong wool',
  'Bin',
  'Interlot',
]

const INSTRUCTION_OPTIONS_INTERNAL = [
  'R sale ML T',
  'M sale ML T',
  'X sale ML T',
  'M54 ML T',
  'M55 DT (hold)',
  'X55 DT (hold)',
  'IL (Sth Islnd)',
  'IL (Nth Islnd)',
  'Fine Wool BIN',
  'BIN Xbd/non-ZQ',
  'DAGS IL',
  'NC',
]

export const BALE_NUMBERS_DEBOUNCE_MS = 500

// Allow bale numbers to be undefined within editor
type SpeciSpeciLineEditorData = Omit<SpeciLine, 'baleNumbers'> & {
  baleNumbers: string
}

type SpeciLineEditorProps = {
  initialData?: SpeciLine
  contractOptions?: string[]
  contractsIsLoading?: boolean
  useInternalInstructions?: boolean
  requireInstructions?: boolean
  onSubmit: (value: SpeciLine) => void
  onCancel?: () => void
  firstFieldRef?: React.RefObject<HTMLInputElement>
}

/**
 * SpeciLineEditor component allows users to add or edit a wool line.
 *
 * @param {SpeciLineEditorProps} props - The properties for the SpeciLineEditor component.
 * @param {SpeciLine} props.initialData - The initial data for the form, used for editing an existing line.
 * @param {string[]} props.contractOptions - Options for the contract selection.
 * @param {boolean} props.contractsIsLoading - Indicates if the contract options are loading.
 * @param {boolean} props.useInternalInstructions - Indicates if internal list of instructions should be used.
 * @param {boolean} props.requireInstructions - Indicates if instructions are required.
 * @param {Function} props.onSubmit - Callback function to handle form submission.
 * @param {Function} props.onClear - Callback function to handle form clear.
 *
 * @returns {JSX.Element} The rendered SpeciLineEditor component.
 */
export function SpeciLineEditor({
  initialData,
  contractOptions,
  contractsIsLoading,
  useInternalInstructions = false,
  requireInstructions = false,
  firstFieldRef,
  onSubmit,
  onCancel,
}: SpeciLineEditorProps) {
  const [balesList, setBalesList] = useState<number[]>([])

  // Form validation schema
  const formSchema = z.object({
    id: z.string().optional(),
    instructions: requireInstructions
      ? z.string().nonempty({ message: 'Instruction is required' })
      : z.string().optional(),
    description: z
      .string()
      .nonempty({ message: 'Line description cannot be empty' })
      .max(15, { message: 'Line description cannot be longer than 15 characters' }),
    traditionalDescription: z
      .string()
      .max(15, { message: 'Traditional description cannot be longer than 15 characters' }),
    baleNumbers: z.preprocess(
      (value) => (typeof value === 'string' ? value.replace(/\s+/g, '') : ''), // Remove all whitespace
      z
        .string()
        .regex(/^(?:\d{1,5}(?:-\d{1,5})?)(?:,\d{1,5}(?:-\d{1,5})?)*$/, {
          message: 'Only whole numbers up to 6 digits, hyphens, commas, and spaces are allowed',
        })
        .nonempty({ message: 'At least one bale number is required' })
    ),
  })

  // use effect to cancel form state when initialData is undefined
  useEffect(() => {
    if (!initialData) {
      handleCancel()
    }
  }, [initialData])

  // Form object
  const form = useForm<SpeciSpeciLineEditorData>({
    mode: 'uncontrolled',
    validateInputOnChange: true,
    initialValues: {
      id: initialData?.id || '',
      number: initialData?.number || '',
      description: initialData?.description || '',
      traditionalDescription: initialData?.traditionalDescription || '',
      instructions: initialData?.instructions || '',
      baleNumbers: parseBalesArrayToString(initialData?.baleNumbers || []),
    },
    // TODO: Raise issue in Mantine forums with codesandbox example to figure out
    // way to have initial form values and transformed values be different types.
    // @ts-ignore
    transformValues: (values) => ({
      ...values,
      baleNumbers: parseBalesStringToArray(values.baleNumbers),
    }),
    validate: zodResolver(formSchema),
  })

  // Component will re-render when the initialData prop changes
  useEffect(() => {
    if (initialData) {
      form.setValues({
        id: initialData?.id || '',
        number: initialData?.number || '',
        description: initialData?.description || '',
        traditionalDescription: initialData?.traditionalDescription || '',
        instructions: initialData?.instructions || '',
        originalInstructions: initialData?.originalInstructions || '',
        baleNumbers: parseBalesArrayToString(initialData?.baleNumbers || []),
      })

      updateBaleNumbers()
    }
  }, [initialData])

  // Clear the form when the clear button is clicked
  const handleCancel = () => {
    form.reset()
    setBalesList([])
    onCancel?.()
  }

  const updateBaleNumbers = () => {
    const bales = form.getTransformedValues().baleNumbers
    setBalesList(Array.isArray(bales) ? bales : [])
  }

  const handleBaleNumbersChange = useDebouncedCallback(updateBaleNumbers, BALE_NUMBERS_DEBOUNCE_MS)

  form.watch('baleNumbers', () => {
    handleBaleNumbersChange()
  })

  return (
    <Paper shadow='sm' p='md' withBorder bd='1px solid gray.2' data-testid='speci-line-editor'>
      <form
        onSubmit={form.onSubmit((values) => {
          // TODO: Raise issue in Mantine forums with codesandbox example to figure out
          // way to have initial form values and transformed values be different types.
          // @ts-ignore
          onSubmit(values)
          form.reset()
          setBalesList([])
        })}
      >
        <Stack>
          <Title order={3}>{initialData ? 'Edit' : 'Enter'} line details</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <TextInput
              {...form.getInputProps('number')}
              key={form.key('number')}
              label='Line number'
              ref={firstFieldRef}
            />
            <TextInput
              {...form.getInputProps('description')}
              key={form.key('description')}
              label='Line description'
            />
            <TextInput
              {...form.getInputProps('traditionalDescription')}
              key={form.key('traditionalDescription')}
              label='Traditional description'
            />
            <Autocomplete
              label='Instructions/Contracts'
              data={[
                {
                  group: 'Instructions',
                  items: useInternalInstructions
                    ? INSTRUCTION_OPTIONS_INTERNAL
                    : INSTRUCTION_OPTIONS_GROWER,
                },
                { group: 'Contracts', items: contractOptions || [] },
              ]}
              description={
                initialData?.originalInstructions
                  ? `Grower instruction: ${initialData?.originalInstructions}`
                  : undefined
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              maxDropdownHeight={200}
              leftSection={contractsIsLoading ? <Loader size={18} /> : <Combobox.Chevron />}
              leftSectionPointerEvents='none'
              clearable
              // Always display dropdown below
              comboboxProps={{ position: 'bottom', middlewares: { flip: false, shift: false } }}
              {...form.getInputProps('instructions')}
              key={form.key('instructions')}
            />
          </SimpleGrid>
          <TextInput
            {...form.getInputProps('baleNumbers')}
            key={form.key('baleNumbers')}
            label='Bale numbers/range'
            description='Enter bale numbers as individual numbers (e.g. "1, 2, 3"), ranges (e.g. "1-5"), or a mix of both (e.g. "1-4, 6, 8-13").'
          />
          <BalesList baleNumbers={balesList} />
          <Group justify='flex-end'>
            <Button onClick={handleCancel} variant='outline' color='gray'>
              Cancel
            </Button>
            <Button type='submit'>{initialData ? 'Save line' : 'Add line'}</Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  )
}
